/**
 * MarketplacePage.tsx
 *
 * Browse pre-built, verified WASM skills from the marketplace.
 *
 * Data: GET /v1/marketplace/skills → { items: MarketplaceSkill[] } (verified,
 * global-scope skills from the wasm_registry).
 *
 * NOTE: "Add to Agent" is intentionally disabled. The platform has no per-agent
 * skill-association endpoint yet (no POST /v1/agents/{id}/skills, no agent↔skill
 * model, and no list-agents endpoint to pick from). The button is a placeholder
 * for that future capability rather than a dead API call.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { api } from '../api/client'
import { GlassPanel } from './common/GlassPanel'

// ── Types (mirror contracts.WasmSkillSummary JSON) ────────────────────────────

interface MarketplaceSkill {
  skillId: string
  version: string
  name: string
  description?: string
  category?: string
  capabilities?: string[]
  inputSchema?: Record<string, unknown>
  iconUrl?: string
}

type LoadState = 'loading' | 'ready' | 'error'

// ── Badges ────────────────────────────────────────────────────────────────────

function CategoryBadge({ category }: { category: string }) {
  return (
    <span className="text-[9.5px] font-bold tracking-[0.1em] uppercase text-[#38bdf8] bg-[rgba(56,189,248,0.1)] border border-[rgba(56,189,248,0.22)] rounded-full px-[8px] py-0.5 whitespace-nowrap">
      {category}
    </span>
  )
}

function CapabilityBadge({ capability }: { capability: string }) {
  return (
    <span className="text-[10px] font-semibold text-[#8fa3bd] bg-[rgba(148,163,184,0.08)] border border-[rgba(148,163,184,0.14)] rounded-md px-[7px] py-0.5 font-mono">
      {capability}
    </span>
  )
}

// ── Skill card ──────────────────────────────────────────────────────────────

function SkillCard({ skill }: { skill: MarketplaceSkill }) {
  return (
    <GlassPanel className="p-5 flex flex-col gap-3 min-h-[180px]">
      {/* Header: name + category */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-[15px] font-[800] text-[#c8d9ee] m-0 tracking-[-0.02em] leading-[1.3]">
          {skill.name}
        </h3>
        {skill.category && <CategoryBadge category={skill.category} />}
      </div>

      {/* Description */}
      <p className="text-[12px] text-[#4e6480] leading-[1.6] m-0 flex-1">
        {skill.description || 'No description provided.'}
      </p>

      {/* Capabilities */}
      {skill.capabilities && skill.capabilities.length > 0 && (
        <div className="flex flex-wrap gap-[6px]">
          {skill.capabilities.map((c) => (
            <CapabilityBadge key={c} capability={c} />
          ))}
        </div>
      )}

      {/* Action — disabled until per-agent skill association exists */}
      <button
        type="button"
        disabled
        title="Coming soon — agent skill association is not yet available"
        className="mt-1 self-start px-[14px] py-[8px] rounded-[9px] border border-[rgba(148,163,184,0.18)] bg-[rgba(148,163,184,0.06)] text-[#4e6480] text-[12px] font-bold cursor-not-allowed opacity-70"
      >
        Add to Agent
      </button>
    </GlassPanel>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

export function MarketplacePage() {
  const [skills, setSkills] = useState<MarketplaceSkill[]>([])
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [category, setCategory] = useState<string>('All')

  const load = useCallback(async () => {
    setLoadState('loading')
    try {
      const data = await api.get<{ items: MarketplaceSkill[] | null }>('/v1/marketplace/skills')
      setSkills(Array.isArray(data.items) ? data.items : [])
      setLoadState('ready')
    } catch {
      setLoadState('error')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Distinct categories from the loaded skills, with an "All" pill first.
  const categories = useMemo(() => {
    const set = new Set<string>()
    for (const s of skills) if (s.category) set.add(s.category)
    return ['All', ...[...set].sort()]
  }, [skills])

  const visibleSkills = useMemo(
    () => (category === 'All' ? skills : skills.filter((s) => s.category === category)),
    [skills, category],
  )

  return (
    <div className="px-8 pt-7 pb-12 max-w-[1100px] relative">
      {/* Page header */}
      <div className="flex items-start justify-between gap-6 mb-9">
        <div className="flex-1 min-w-0">
          <h1 className="text-[22px] font-[900] text-[#dce9f7] m-0 mb-2 tracking-[-0.03em]">Marketplace</h1>
          <p className="text-[13px] text-[#4e6480] leading-[1.65] m-0 max-w-[520px]">
            Browse pre-built, verified skills you can give your agents — from data
            extraction to notifications. Each runs sandboxed within your tenant's
            permission scope.
          </p>
        </div>
        {loadState === 'ready' && (
          <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
            <span className="text-[32px] font-[900] text-[#38bdf8] leading-none tabular-nums">{skills.length}</span>
            <span className="text-[11px] font-semibold text-[#4e6480] tracking-[0.06em] uppercase">
              {skills.length === 1 ? 'skill' : 'skills'} available
            </span>
          </div>
        )}
      </div>

      {/* Loading */}
      {loadState === 'loading' && (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="rounded-2xl border border-[rgba(148,163,184,0.08)] bg-[rgba(11,18,32,0.5)] min-h-[180px] animate-pulse"
              aria-hidden="true"
            />
          ))}
        </div>
      )}

      {/* Error */}
      {loadState === 'error' && (
        <div className="text-[13px] text-[#f87171] flex items-center gap-2 px-[18px] py-[14px] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-xl">
          <span aria-hidden="true">⚠</span>
          Failed to load marketplace skills.{' '}
          <button
            className="bg-transparent border-none text-[#f87171] text-[13px] font-[inherit] cursor-pointer underline p-0 hover:text-[#fca5a5]"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty */}
      {loadState === 'ready' && skills.length === 0 && (
        <div className="text-[13px] text-[#4e6480] leading-[1.65] px-[18px] py-[16px] bg-[rgba(148,163,184,0.05)] border border-[rgba(148,163,184,0.1)] rounded-xl">
          No marketplace skills available. Run{' '}
          <code className="font-mono text-[#8fa3bd] bg-[rgba(6,10,16,0.6)] px-[6px] py-0.5 rounded">make marketplace-smoke</code>{' '}
          to seed the marketplace.
        </div>
      )}

      {/* Category filter pills */}
      {loadState === 'ready' && skills.length > 0 && categories.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {categories.map((c) => {
            const active = c === category
            return (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={[
                  'px-[12px] py-[5px] rounded-full text-[11.5px] font-bold border cursor-pointer transition-colors duration-[120ms] capitalize',
                  active
                    ? 'bg-[rgba(56,189,248,0.16)] border-[rgba(56,189,248,0.5)] text-[#38bdf8]'
                    : 'bg-[rgba(148,163,184,0.06)] border-[rgba(148,163,184,0.16)] text-[#8fa3bd] hover:bg-[rgba(148,163,184,0.12)] hover:text-[#c8d9ee]',
                ].join(' ')}
              >
                {c}
              </button>
            )
          })}
        </div>
      )}

      {/* Grid */}
      {loadState === 'ready' && skills.length > 0 && (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {visibleSkills.map((s) => (
            <SkillCard key={`${s.skillId}@${s.version}`} skill={s} />
          ))}
        </div>
      )}
    </div>
  )
}
